// copied and edited from packages/ui/src/components/FlavorFinder/components/OptionalQuestion/FlavorFinderOptionalQuestion.tsx
import type { FC } from "react";
import { useCallback, useState } from "react";

import { ResponsiveImage } from "@ui/components/ResponsiveImage";

import { Button, RadioButton, RadioButtonGroup } from "@jti/core-components";

import type {
  FlavorFinderAdditionalQuestionDetails,
  FlavorFinderOptionalQuestionTransitions,
  Step,
} from "../../types/FlavorFinderTypes";
import { FlavorFinderBackButton, FlavorFinderSkipButton } from "../Buttons/FlavorFinderButtons";
import { FlavorFinderFooter, FlavorFinderFooterSticky } from "../Footer/FlavorFinderFooter";
import { FlavorFinderHeader } from "../Header/FlavorFinderHeader";
import { useFlavorFinderContext } from "../Provider/FlavorFinderContextProvider";

import styles from "./FlavorFinderOptionalQuestion.module.scss";

type FlavorFinderAdditionalProps = {
  question: FlavorFinderOptionalQuestionTransitions;
  nextCtaLabel?: string;
  backCtaLabel: string;
  skipCtaLabel: string;
  selectionIndicator?: string;
  hideNextButton?: boolean;
  continueCtaLabel?: string;
  errorText?: string;
};

export const FlavorFinderOptionalQuestion: FC<FlavorFinderAdditionalProps> = ({
  question,
  backCtaLabel,
  skipCtaLabel,
  selectionIndicator,
  continueCtaLabel,
  errorText,
}) => {
  const { question: questionDetails, possibleAnswers } = question;
  const { userJourney, setUserJourney, setCurrentView, handlePreviousStepClick, setCurrentQuestionIndex } =
    useFlavorFinderContext();
  const [showValidationError, setShowValidationError] = useState(false);

  const selectedAnswerIdForCurrentQuestion = userJourney.find(
    (step) => step.question?.question?.id === questionDetails.id
  );
  const handleNextStepClick = useCallback(() => {
    if (!selectedAnswerIdForCurrentQuestion?.answer_id) {
      setShowValidationError(true);
      return;
    }

    setCurrentQuestionIndex((prevIndex) => prevIndex + 1);
    setUserJourney([...userJourney, { view: "consent" }]);
    setCurrentView("consent");
  }, [
    selectedAnswerIdForCurrentQuestion?.answer_id,
    setCurrentView,
    setCurrentQuestionIndex,
    setUserJourney,
    userJourney,
  ]);

  const handleAnswerSelect = (answerId: string) => () => {
    const updatedJourney = [...userJourney];
    const currentStepIndex = updatedJourney.findIndex((step) => step.question?.question?.id === questionDetails.id);
    const selectedAnswer = possibleAnswers.find((answer) => answer.id === answerId);

    if (currentStepIndex > -1) {
      updatedJourney[currentStepIndex].answer_id = answerId;
      updatedJourney[currentStepIndex].reference_name = selectedAnswer?.referenceName;
      updatedJourney[currentStepIndex].local_translation = selectedAnswer?.localTranslation;
    } else {
      updatedJourney.push({
        view: "optional",
        question: question,
        answer_id: answerId,
        reference_name: selectedAnswer?.referenceName,
        local_translation: selectedAnswer?.localTranslation,
      });
    }

    setUserJourney(updatedJourney);
    setShowValidationError(false);
  };

  const findStepIndex = (journey: Step[], questionId: string): number => {
    const index = journey.findIndex((step) => step.question?.question?.id === questionId);
    if (index === -1) {
      throw new Error(`Step not found for question ID: ${questionId}`);
    }
    return index;
  };

  const handleSkipClick = useCallback(() => {
    setCurrentQuestionIndex((prevIndex) => prevIndex + 1);

    setUserJourney((prevJourney) => {
      try {
        const updatedJourney = [...prevJourney];
        const currentStepIndex = findStepIndex(updatedJourney, questionDetails.id);

        if (updatedJourney[currentStepIndex]) {
          delete updatedJourney[currentStepIndex].answer_id;
        }

        return [...updatedJourney, { view: "consent" }];
      } catch {
        console.error("Error updating journey");
        return prevJourney;
      }
    });

    setCurrentView("consent");
  }, [questionDetails.id]);

  return (
    <div className={styles.questionsWrapperAdditional} data-testid="flavor-finder-additional-question">
      <div className={styles.questionContainer}>
        <div className={styles.question}>
          <FlavorFinderHeader infoText={`${questionDetails.indicatorOverrideLabel}`}>
            <FlavorFinderBackButton onClick={handlePreviousStepClick} label={backCtaLabel} mobileHidden />
          </FlavorFinderHeader>
          <div className={styles.additionalQuestionContent}>
            <div className={styles.panel}>
              <h3 className={styles.panelHeader}>{questionDetails.title}</h3>
              {selectionIndicator && <p className={styles.panelSubheader}>{selectionIndicator}</p>}
              <RadioButtonGroup name="answerGroup" isError={showValidationError} errorText={errorText}>
                {possibleAnswers.map((answer: FlavorFinderAdditionalQuestionDetails) => (
                  <RadioButton
                    key={answer.id}
                    value={answer.id}
                    label={answer.title}
                    checked={selectedAnswerIdForCurrentQuestion?.answer_id === answer.id}
                    onChange={(event) => handleAnswerSelect(event.target.value)()}
                  />
                ))}
              </RadioButtonGroup>
            </div>
            {questionDetails.image ? (
              <ResponsiveImage
                alt={questionDetails.alt}
                largeSrc={questionDetails.image}
                mediumSrc={questionDetails.mediumImage}
                smallSrc={questionDetails.smallImage}
                className={styles.responsiveImageContainer}
              />
            ) : null}
          </div>
          <FlavorFinderFooter>
            <FlavorFinderBackButton
              onClick={handlePreviousStepClick}
              className={styles.backButton}
              label={backCtaLabel}
            />
            <FlavorFinderFooterSticky>
              <Button className={styles.button} onClick={handleNextStepClick} variant="primary">
                {continueCtaLabel}
              </Button>
              <FlavorFinderSkipButton
                label={skipCtaLabel}
                onClick={handleSkipClick}
                className={styles.skipStickyButton}
              />
            </FlavorFinderFooterSticky>
          </FlavorFinderFooter>
        </div>
      </div>
    </div>
  );
};
